import PropTypes from 'prop-types';
import React, { useState, useEffect, createContext } from 'react';
// material
import { alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import defaultLogo from '../images/logo.svg';
import defaultFavicon from '../images/favicon/favicon-96x96.png';
import { getDomainOrSubdomain, isDefaultRoute } from '../helpers/Util';
import { getEvent } from '../services/Event';
import Loader from '../components/Loader';

export const SettingsContext = createContext();

SettingsProvider.propTypes = {
  settings: PropTypes.object,
  children: PropTypes.node.isRequired
};

export default function SettingsProvider({ settings, children }) {
  const route = getDomainOrSubdomain();

  const [settingsId, setSettingsId] = useState();
  const [logo, setLogo] = useState({});
  const [favicon, setFavicon] = useState({});
  const [themeSettings, setThemeSettings] = useState({});
  const [metatags, setMetatags] = useState({});
  const [authSettings, setAuthSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const createGradient = (color) => {
      const red = parseInt(color[1] + color[2], 16) + 50;
      const green = parseInt(color[3] + color[4], 16) + 50;
      const blue = parseInt(color[5] + color[6], 16) + 50;
      const lightColor = `rbg(${red}, ${green}, ${blue})`;
      return `linear-gradient(to bottom, ${lightColor}, ${color})`;
    };

    const createCustomShadow = (color) => `0 8px 16px 0 ${alpha(color, 0.24)}`;

    const isProducerRoute = () =>
      settings?.route?.producer?.domain === route || settings?.route?.producer?.subdomain === route;

    const setDefaultSettings = async () => {
      const themeColor = process.env.REACT_APP_THEME_COLOR;

      setThemeSettings({
        palette: {
          primary: {
            main: themeColor
          },
          gradients: {
            primary: createGradient(themeColor)
          }
        },
        customShadows: {
          primary: createCustomShadow(themeColor)
        }
      });
      setLogo({
        regular: {
          url: defaultLogo
        }
      });
      setFavicon({
        url: defaultFavicon
      });
      setMetatags({
        title: process.env.REACT_APP_METATAGS_TITLE,
        description: process.env.REACT_APP_METATAGS_DESCRIPTION
      });
      setAuthSettings({
        server: {
          path: process.env.REACT_APP_FIREBASE_ACCOUNT_KEY_PATH
        }
      });
      setIsLoading(false);
    };

    const setProducerSettings = () => {
      const themeColor = settings?.theme?.color || process.env.REACT_APP_THEME_COLOR;

      setThemeSettings({
        palette: {
          primary: {
            main: themeColor
          },
          gradients: {
            primary: createGradient(themeColor)
          }
        },
        customShadows: {
          primary: createCustomShadow(themeColor)
        }
      });
      setLogo({
        regular: {
          url: settings?.theme?.logo?.regular?.url || defaultLogo
        }
      });
      setFavicon({
        url: settings?.theme?.favicon?.url || defaultFavicon
      });
      setMetatags({
        title: settings?.metatags?.title || process.env.REACT_APP_METATAGS_TITLE,
        description: settings?.metatags?.description || process.env.REACT_APP_METATAGS_DESCRIPTION
      });
      setAuthSettings({
        server: {
          path: settings?.auth?.server?.path
        }
      });
      setIsLoading(false);
    };

    const setEventSettings = async () => {
      try {
        const event = await getEvent();
        const themeColor =
          event?.theme?.color || settings?.theme?.color || process.env.REACT_APP_THEME_COLOR;

        setThemeSettings({
          palette: {
            primary: {
              main: themeColor
            },
            gradients: {
              primary: createGradient(themeColor)
            }
          },
          customShadows: {
            primary: createCustomShadow(themeColor)
          }
        });
        setLogo({
          regular: {
            url:
              event?.theme?.logo?.regular?.url || settings?.theme?.logo?.regular?.url || defaultLogo
          }
        });
        setFavicon({
          url: event?.theme?.favicon?.url || settings?.theme?.favicon?.url || defaultFavicon
        });
        setMetatags({
          title:
            event.metatags?.title ||
            settings?.metatags?.title ||
            process.env.REACT_APP_METATAGS_TITLE,
          description:
            event?.metatags?.description ||
            settings?.metatags?.description ||
            process.env.REACT_APP_METATAGS_DESCRIPTION
        });
        setAuthSettings({
          server: {
            path: settings?.auth?.server?.path
          }
        });
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        alert(
          'An error occurred while trying to load the event — Please, try to refresh the page!'
        );
      }
    };

    setSettingsId(settings._id);

    if (isDefaultRoute()) {
      setDefaultSettings();
    } else if (isProducerRoute()) {
      setProducerSettings();
    } else {
      setEventSettings();
    }
  }, [settings, route]);

  if (isLoading) {
    return <Loader align="center" />;
  }

  return (
    <SettingsContext.Provider value={{ authSettings, themeSettings, metatags, logo, settingsId }}>
      <Helmet>
        <link rel="apple-touch-icon" sizes="57x57" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="60x60" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="72x72" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="76x76" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="114x114" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="120x120" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="144x144" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="152x152" href={favicon.url} />
        <link rel="apple-touch-icon" sizes="180x180" href={favicon.url} />
        <link rel="icon" type="image/png" sizes="192x192" href={favicon.url} />
        <link rel="icon" type="image/png" sizes="32x32" href={favicon.url} />
        <link rel="icon" type="image/png" sizes="96x96" href={favicon.url} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon.url} />
        <link rel="icon" type="image/png" href={favicon.url} />

        <meta name="msapplication-TileColor" content={themeSettings.palette.primary.main} />
        <meta name="msapplication-TileImage" content={favicon.url} />

        <meta name="theme-color" content={themeSettings.palette.primary.main} />
        <meta name="description" content={metatags.description} />
      </Helmet>
      {!isLoading && children}
    </SettingsContext.Provider>
  );
}
